/**
 * The MLLP channel table. "MLLP channel" and not just "channel": in this
 * codebase `channel` already means a webhook subscription channel, and
 * `Integration` means a tenant's instrument/EHR connection. This is neither —
 * it is a process-level TCP listener or sender from the harness's channels.json.
 *
 * Every value here comes off the wire. No port, host or channel id is hardcoded:
 * the shipped table still carries placeholder ports pending the AWS assignment,
 * so anything literal in here would be wrong the day that changes.
 */
import { Badge } from '@/components/ui/badge'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import {
  channelEndpoint,
  channelStateLabel,
  channelTone,
  formatAcks,
  type ChannelTone,
} from '@/lib/dataLayer/channelView'
import type { Hl7Channel } from '@/lib/dataLayer/types'

// badge.tsx ships only default/secondary/destructive/outline, so status colour
// is a classname map — the same call WebhooksPanel's OUTCOME_STYLE makes.
const TONE_DOT: Record<ChannelTone, string> = {
  green: 'bg-green-500',
  red: 'bg-red-500',
  grey: 'bg-muted-foreground/40',
}

// Per-user column layout. Channel is locked — it identifies the row. See
// src/components/table/index.ts for the pattern.
const CHANNELS_TABLE_ID = 'hl7-channels'

const CHANNELS_COLUMNS: TableColumnDef[] = [
  { id: 'channel', header: 'Channel', locked: true, sortable: true },
  { id: 'direction', header: 'Direction', sortable: true },
  { id: 'types', header: 'Types', sortable: true },
  { id: 'endpoint', header: 'Endpoint', sortable: true },
  { id: 'messages', header: 'Messages', sortable: true },
  { id: 'errors', header: 'Errors', sortable: true },
  { id: 'acks', header: 'ACKs', sortable: true },
  { id: 'lastPeer', header: 'Last peer', sortable: true },
]

// Column sorting (z8nrz7e4h6): the counters numerically, the rest as they read.
const CHANNELS_SORT: SortAccessors<Hl7Channel> = {
  channel: (c) => c.id,
  direction: (c) => (c.direction === 'in' ? 'inbound' : 'outbound'),
  types: (c) => c.types.join(', '),
  endpoint: (c) => channelEndpoint(c),
  messages: (c) => c.messages,
  errors: (c) => c.errors,
  acks: (c) => formatAcks(c.acks),
  lastPeer: (c) => c.lastPeer,
}

const CHANNEL_HEAD_CLASSES: Record<string, string | undefined> = {
  messages: 'text-right',
  errors: 'text-right',
}

export function ChannelTable({ channels }: { channels: Hl7Channel[] }) {
  // Before the empty-state early return below: a hook cannot be conditional.
  const cols = useTableColumns(CHANNELS_TABLE_ID, CHANNELS_COLUMNS)
  const rows = useSortedRows(channels, cols.sort, CHANNELS_SORT)
  if (channels.length === 0) {
    return (
      <p className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
        The data layer reports no channels.
      </p>
    )
  }

  return (
    <>
    <Table>
      <TableHeader>
        <TableRow>
          {cols.visible.map(({ def }) => (
            <ResizableTableHead
              key={def.id}
              columnId={def.id}
              columns={cols}
              label={def.header}
              className={CHANNEL_HEAD_CLASSES[def.id]}
            >
              {def.header}
            </ResizableTableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((channel) => {
          const tone = channelTone(channel)
          const acks = formatAcks(channel.acks)
          return (
            <TableRow key={channel.id}>
              <ColumnCells
                columns={cols}
                cells={{
                  channel: (
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        <span
                          className={cn('h-2 w-2 shrink-0 rounded-full', TONE_DOT[tone])}
                          title={channel.lastError ?? channelStateLabel(channel)}
                        />
                        <code className="font-mono text-xs">{channel.id}</code>
                      </span>
                    </TableCell>
                  ),
                  direction: (
                    <TableCell>
                      <Badge variant="secondary">{channel.direction === 'in' ? 'inbound' : 'outbound'}</Badge>
                    </TableCell>
                  ),
                  types: (
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {channel.types.join(', ')}
                    </TableCell>
                  ),
                  endpoint: (
                    <TableCell className="font-mono text-xs">
                      {channelEndpoint(channel)}
                      {channel.direction === 'out' && (
                        // Read-only on purpose: they belong to the channel table so a
                        // run's evidence can state what timeout was in force. The
                        // Iguana Simulator lets an operator type them; ours does not.
                        <span className="ml-2 text-[11px] text-muted-foreground">
                          timeout {channel.timeoutMs}ms · {channel.retries} retr
                          {channel.retries === 1 ? 'y' : 'ies'}
                        </span>
                      )}
                    </TableCell>
                  ),
                  messages: <TableCell className="text-right font-mono text-xs">{channel.messages}</TableCell>,
                  errors: (
                    <TableCell
                      className={cn(
                        'text-right font-mono text-xs',
                        channel.errors > 0 ? 'text-destructive' : 'text-muted-foreground',
                      )}
                    >
                      {channel.errors}
                    </TableCell>
                  ),
                  acks: <TableCell className="font-mono text-xs text-muted-foreground">{acks ?? '—'}</TableCell>,
                  lastPeer: (
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {channel.lastPeer ?? '—'}
                    </TableCell>
                  ),
                }}
              />
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
    </>
  )
}
